import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isPermanentPeriod } from '../../constants/groupPeriods';
import { formatPeriodDateRange } from '../../utils/formatPeriodDates';
import { periodLabel } from '../../utils/orgGroupTree';

export function formatTeamGroupsPeriodOption(gp) {
  if (!gp) return 'Period';
  const label = periodLabel({
    period_title: gp.period_title,
    title: gp.title,
    period_code: gp.period_code,
    code: gp.period_code
  });
  const range = formatPeriodDateRange(gp.start_date, gp.end_date);
  return range ? `${label} (${range})` : label;
}

function attachGpIsPermanent(gp) {
  if (!gp) return false;
  return (
    isPermanentPeriod(gp) ||
    isPermanentPeriod({
      title: gp.period_title,
      code: gp.period_code,
      is_permanent: gp.is_permanent
    })
  );
}

function attachPeriodBadgeMeta(gp) {
  if (!gp) return '';
  if (attachGpIsPermanent(gp)) return 'Permanent · open-ended';
  const range = formatPeriodDateRange(gp.start_date, gp.end_date);
  const code = gp.period_code && gp.period_code !== gp.period_title ? gp.period_code : '';
  if (range && code) return `${code} · ${range}`;
  return range || code || '';
}

function attachPeriodBadgeTitle(gp) {
  if (!gp) return 'Select period…';
  return (
    periodLabel({
      period_title: gp.period_title,
      title: gp.title,
      period_code: gp.period_code,
      code: gp.period_code
    }) || 'Period'
  );
}

/**
 * Shown when adding under a focused team: another groups_period vs nested group.
 * Nested group attaches to a parent groups-period (badge picker).
 */
const GroupsTeamAddContextFields = ({
  anchorGroup,
  addMode = 'team_period',
  onAddModeChange,
  parentAttachGpId = '',
  onParentAttachChange,
  disabled = false
}) => {
  const teamName = anchorGroup?.name || 'this team';
  const attachOptions = anchorGroup?.periods || [];
  const attachGp =
    attachOptions.find((gp) => String(gp.id) === String(parentAttachGpId)) || null;
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const badgeRef = useRef(null);
  const menuRef = useRef(null);
  const canNest = attachOptions.length > 0;

  useLayoutEffect(() => {
    if (!menuOpen || !badgeRef.current) {
      setMenuPos(null);
      return undefined;
    }
    const updatePos = () => {
      const rect = badgeRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.max(rect.width, 180);
      const left = Math.min(rect.left, window.innerWidth - width - 8);
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 200 && rect.top > spaceBelow;
      setMenuPos({
        top: openUp ? undefined : rect.bottom + 4,
        bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
        left: Math.max(8, left),
        width
      });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [menuOpen, parentAttachGpId]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (badgeRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    // click (not mousedown) so option onClick runs first without fighting the closer
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [menuOpen]);

  useEffect(() => {
    if (addMode !== 'nested_team') setMenuOpen(false);
  }, [addMode]);

  const selectAttachGp = (gpId) => {
    setMenuOpen(false);
    if (addMode !== 'nested_team') onAddModeChange?.('nested_team');
    if (String(gpId) !== String(parentAttachGpId)) {
      onParentAttachChange?.(String(gpId));
    }
  };

  return (
    <div className="groups-team-add-mode-block">
      <div
        className="groups-team-add-mode"
        role="radiogroup"
        aria-label={`Add under ${teamName}`}
      >
        <button
          type="button"
          role="radio"
          aria-checked={addMode === 'team_period'}
          className={`groups-team-add-mode-card${addMode === 'team_period' ? ' is-selected' : ''}`}
          onClick={() => onAddModeChange?.('team_period')}
          disabled={disabled}
        >
          <span className="groups-team-add-mode-card-title">Another period</span>
          <span className="groups-team-add-mode-card-note">
            Same group, new time period on <strong>{teamName}</strong>.
          </span>
        </button>

        <div
          role="radio"
          aria-checked={addMode === 'nested_team'}
          tabIndex={disabled || !canNest ? -1 : 0}
          className={`groups-team-add-mode-card${addMode === 'nested_team' ? ' is-selected' : ''}${
            !canNest ? ' is-disabled' : ''
          }`}
          onClick={() => {
            if (disabled || !canNest) return;
            onAddModeChange?.('nested_team');
          }}
          onKeyDown={(e) => {
            if (disabled || !canNest) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onAddModeChange?.('nested_team');
            }
          }}
        >
          <span className="groups-team-add-mode-card-title">Nested Group</span>
          <span className="groups-team-add-mode-card-note">
            New group under <strong>{teamName}</strong> for this period.
          </span>
          {canNest ? (
            <div
              className="groups-team-add-mode-period-picker"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                ref={badgeRef}
                className={`groups-team-add-mode-period-badge${menuOpen ? ' is-open' : ''}${
                  attachGp ? ' has-value' : ''
                }${attachGpIsPermanent(attachGp) ? ' is-permanent' : ''}`}
                aria-haspopup="listbox"
                aria-expanded={menuOpen}
                aria-label="Parent groups-period to nest under"
                onClick={(e) => {
                  e.stopPropagation();
                  if (disabled) return;
                  if (addMode !== 'nested_team') onAddModeChange?.('nested_team');
                  setMenuOpen((o) => !o);
                }}
                disabled={disabled}
              >
                <span className="groups-team-add-mode-period-badge-main">
                  <span className="groups-team-add-mode-period-badge-title">
                    {attachPeriodBadgeTitle(attachGp)}
                  </span>
                  {attachGp && attachPeriodBadgeMeta(attachGp) ? (
                    <span className="groups-team-add-mode-period-badge-meta">
                      {attachPeriodBadgeMeta(attachGp)}
                    </span>
                  ) : null}
                </span>
                <span className="groups-team-add-mode-period-badge-caret" aria-hidden>
                  ▾
                </span>
              </button>
            </div>
          ) : (
            <span className="groups-team-add-mode-card-note">
              This team has no periods yet. Add another period first.
            </span>
          )}
        </div>
      </div>

      {addMode === 'nested_team' && canNest ? (
        <p className="groups-team-add-mode-hint">
          Click the period badge to change to another period if needed.
        </p>
      ) : null}

      {menuOpen && menuPos
        ? createPortal(
            <ul
              ref={menuRef}
              className="groups-team-add-mode-period-menu"
              role="listbox"
              aria-label={`Periods on ${teamName}`}
              style={{
                position: 'fixed',
                top: menuPos.top,
                bottom: menuPos.bottom,
                left: menuPos.left,
                width: menuPos.width,
                zIndex: 1100
              }}
            >
              {attachOptions.map((gp) => {
                const active = String(gp.id) === String(parentAttachGpId);
                const permanent = attachGpIsPermanent(gp);
                return (
                  <li key={gp.id} role="option" aria-selected={active}>
                    <button
                      type="button"
                      className={`groups-team-add-mode-period-option${
                        active ? ' is-selected' : ''
                      }${permanent ? ' is-permanent' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAttachGp(gp.id);
                      }}
                    >
                      <span className="groups-team-add-mode-period-option-title">
                        {attachPeriodBadgeTitle(gp)}
                      </span>
                      <span className="groups-team-add-mode-period-option-meta">
                        {attachPeriodBadgeMeta(gp)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>,
            document.body
          )
        : null}
    </div>
  );
};

export default GroupsTeamAddContextFields;
